import { useState } from "react";
import { colorForSeries, formatCompactNumber } from "../../lib/palette";
import type { PivotedChart } from "../../lib/pivot";

interface PieChartProps {
	chart: PivotedChart;
	size?: number;
}

const GAP_DEGREES = 1.5;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
	const angleRad = ((angleDeg - 90) * Math.PI) / 180;
	return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

function arcPath(
	cx: number,
	cy: number,
	r: number,
	startAngle: number,
	endAngle: number,
) {
	const start = polarToCartesian(cx, cy, r, endAngle);
	const end = polarToCartesian(cx, cy, r, startAngle);
	const largeArc = endAngle - startAngle > 180 ? 1 : 0;
	return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

export function PieChart({ chart, size = 200 }: PieChartProps) {
	const [hover, setHover] = useState<number | null>(null);
	// Pie/donut has no x-axis: pivotChartData puts each slice in its own
	// series (one point each), so each series *is* a slice here.
	const slices = chart.series.map((s) => ({
		x: s.name,
		y: s.points[0]?.y ?? 0,
	}));
	const total = slices.reduce((sum, p) => sum + Math.max(0, p.y), 0);

	if (slices.length === 0 || total === 0) {
		return (
			<div className="text-xs font-mono text-base-content/50 py-6 text-center">
				No data for this selection.
			</div>
		);
	}

	const cx = size / 2;
	const cy = size / 2;
	const r = size / 2 - 4;
	let cursor = 0;
	const arcs = slices.map((p, i) => {
		const fraction = Math.max(0, p.y) / total;
		const startAngle = cursor * 360 + GAP_DEGREES / 2;
		cursor += fraction;
		const endAngle = cursor * 360 - GAP_DEGREES / 2;
		return {
			name: p.x,
			value: p.y,
			fraction,
			startAngle: Math.min(startAngle, endAngle),
			endAngle: Math.max(startAngle, endAngle),
			color: colorForSeries(p.x, i),
		};
	});

	return (
		<div className="flex items-center gap-4 flex-wrap">
			<svg
				viewBox={`0 0 ${size} ${size}`}
				width={size}
				height={size}
				className="shrink-0"
			>
				<title>
					Pie chart breakdown by {arcs.map((arc) => arc.name).join(", ")}
				</title>
				{arcs.map((arc, i) => (
					<path
						key={arc.name}
						d={arcPath(cx, cy, r, arc.startAngle, arc.endAngle)}
						fill={arc.color}
						opacity={hover === null || hover === i ? 1 : 0.4}
						onPointerMove={() => setHover(i)}
						onPointerLeave={() => setHover(null)}
					/>
				))}
			</svg>
			<div className="flex flex-col gap-1.5 text-[11px] font-mono min-w-0">
				{arcs.map((arc, i) => (
					<div
						key={arc.name}
						className="flex items-center gap-1.5 cursor-default"
						onPointerEnter={() => setHover(i)}
						onPointerLeave={() => setHover(null)}
						style={{ opacity: hover === null || hover === i ? 1 : 0.5 }}
					>
						<span
							className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
							style={{ backgroundColor: arc.color }}
						/>
						<span className="text-base-content/80 truncate">{arc.name}</span>
						<span className="text-base-content font-semibold tabular-nums ml-auto pl-2">
							{(arc.fraction * 100).toFixed(1)}%
						</span>
						<span className="text-base-content/50 tabular-nums">
							{formatCompactNumber(arc.value)}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}
